// Call performance report model.
//
// One shape feeds the whole Reporting → Calls tab, whichever source filled it:
//   - the backend aggregation endpoint (Elevate call-history, authoritative,
//     `source: 'analytics'`), or
//   - a client-side build from the Call Logs rows already loaded for the
//     window (`source: 'webhook'`), used until that endpoint exists on the
//     target environment. Both are normalised here so the page never branches.
//
// Numbers follow the client's monthly deck: incoming = inbound phone calls to
// the practice's lines; answered = outcome answered; unanswered = everything
// else that was inbound and finished; outgoing = outbound phone calls.
// Hour buckets are Europe/London.

export const REPORT_HOURS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19];
export const LUNCH_HOURS = [12, 13, 14];

const pct = (part, whole) => (whole > 0 ? Math.round((part / whole) * 1000) / 10 : 0);
const avg = (values) => {
  const list = values.filter((v) => Number.isFinite(v));
  return list.length ? Math.round(list.reduce((a, b) => a + b, 0) / list.length) : 0;
};

export const londonHour = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const text = d.toLocaleString('en-GB', { timeZone: 'Europe/London', hour: '2-digit', hour12: false });
  const hour = parseInt(text, 10);
  return Number.isFinite(hour) ? hour % 24 : null;
};

export const formatSeconds = (seconds) => {
  const s = Math.max(0, Math.round(seconds || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r ? `${m}m ${r}s` : `${m}m`;
};

const emptyHour = (hour) => ({ hour, label: `${String(hour).padStart(2, '0')}:00`, calls: 0, answered: 0, unanswered: 0, rate: 0, direct_calls: 0, direct_answered: 0, direct_rate: 0 });

// Build the model from normalised Call Logs rows (see callLog.normalize.js).
export function buildPerformanceFromCalls(rows, { practiceName = '', start = null, end = null } = {}) {
  const phone = rows.filter((r) => r.channel === 'phone' && !r.isTest);
  const inbound = phone.filter((r) => r.direction === 'inbound');
  const outbound = phone.filter((r) => r.direction && r.direction.startsWith('outbound'));
  const answered = inbound.filter((r) => r.outcome === 'answered');
  const unanswered = inbound.filter((r) => r.outcome !== 'answered');

  const lines = new Map();
  inbound.forEach((r) => {
    const key = r.siteNumber || r.numberLabel || 'unknown';
    const line = lines.get(key) || { key, number: r.siteNumber || '', label: r.siteLabel || r.numberLabel || r.siteNumber || 'Main line', calls: 0, answered: 0, missed: 0, rate: 0 };
    line.calls += 1;
    if (r.outcome === 'answered') line.answered += 1; else line.missed += 1;
    lines.set(key, line);
  });
  const byLine = [...lines.values()].map((l) => ({ ...l, rate: pct(l.answered, l.calls) })).sort((a, b) => b.calls - a.calls);

  const hours = new Map(REPORT_HOURS.map((h) => [h, emptyHour(h)]));
  phone.forEach((r) => {
    const h = londonHour(r.timestamp);
    if (h == null) return;
    if (!hours.has(h)) hours.set(h, emptyHour(h));
    const bucket = hours.get(h);
    if (r.direction === 'inbound') {
      bucket.calls += 1;
      if (r.outcome === 'answered') bucket.answered += 1; else bucket.unanswered += 1;
    } else if (r.direction && r.direction.startsWith('outbound')) {
      bucket.direct_calls += 1;
      if (r.outcome === 'answered') bucket.direct_answered += 1;
    }
  });
  const byHour = [...hours.values()].sort((a, b) => a.hour - b.hour).map((b) => ({ ...b, rate: pct(b.answered, b.calls), direct_rate: pct(b.direct_answered, b.direct_calls) }));

  const extensions = new Map();
  phone.forEach((r) => {
    if (!r.userName) return;
    const ext = extensions.get(r.userName) || { name: r.userName, incoming: 0, outgoing: 0 };
    if (r.direction === 'inbound') ext.incoming += 1; else ext.outgoing += 1;
    extensions.set(r.userName, ext);
  });
  const byExtension = [...extensions.values()].sort((a, b) => (b.incoming + b.outgoing) - (a.incoming + a.outgoing));

  const directAnswered = outbound.filter((r) => r.outcome === 'answered');
  return {
    source: 'webhook',
    practice_name: practiceName,
    coverage: { start, end, calls: phone.length },
    incoming: {
      total: inbound.length,
      answered: answered.length,
      unanswered: unanswered.length,
      answer_rate: pct(answered.length, inbound.length),
      overflowed: null, // only the call-history source knows this
      by_line: byLine,
    },
    outgoing: {
      total: outbound.length,
      extensions: new Set(outbound.map((r) => r.userName).filter(Boolean)).size,
      answered: directAnswered.length,
      answer_rate: pct(directAnswered.length, outbound.length),
    },
    averages: {
      ring_seconds: avg(inbound.map((r) => r.ringingSeconds)),
      incoming_length_seconds: avg(answered.map((r) => r.durationSec)),
      outgoing_length_seconds: avg(directAnswered.map((r) => r.durationSec)),
      direct_answer_rate: pct(directAnswered.length, outbound.length),
    },
    by_hour: byHour,
    by_extension: byExtension,
  };
}

// Normalise a backend payload (or a webhook build) so every consumer can rely
// on the same keys and derived values being present.
export function normalisePerformance(raw, fallbackName = '') {
  if (!raw) return null;
  const inc = raw.incoming || {};
  const out = raw.outgoing || {};
  const hours = new Map(REPORT_HOURS.map((h) => [h, emptyHour(h)]));
  (raw.by_hour || []).forEach((b) => {
    const hour = Number(b.hour);
    // The backend reports all 24 hours; the report shows 08:00–19:00 plus any
    // other hour that actually had calls. `direct_calls` is null when the
    // provider's direct-usage stream is missing; outgoing_calls still counts.
    const direct = b.direct_calls ?? b.outgoing_calls ?? 0;
    const active = (b.calls || 0) + direct > 0;
    if (!REPORT_HOURS.includes(hour) && !active) return;
    hours.set(hour, {
      ...emptyHour(hour), ...b, hour, label: b.label || emptyHour(hour).label,
      calls: b.calls || 0, answered: b.answered || 0, unanswered: b.unanswered ?? Math.max(0, (b.calls || 0) - (b.answered || 0)),
      direct_calls: direct, direct_answered: b.direct_answered || 0,
      rate: b.rate ?? pct(b.answered || 0, b.calls || 0),
      direct_rate: b.direct_rate ?? pct(b.direct_answered || 0, direct),
    });
  });
  const byHour = [...hours.values()].sort((a, b) => a.hour - b.hour);
  const rank = raw.rank && raw.rank.position != null ? raw.rank : null;
  return {
    source: raw.source || 'analytics',
    practice_name: raw.practice_name || fallbackName,
    coverage: raw.coverage || null,
    incoming: {
      total: inc.total || 0,
      answered: inc.answered || 0,
      unanswered: inc.unanswered ?? Math.max(0, (inc.total || 0) - (inc.answered || 0)),
      answer_rate: inc.answer_rate ?? pct(inc.answered || 0, inc.total || 0),
      overflowed: inc.overflowed ?? null,
      by_line: (inc.by_line || []).map((l) => ({ ...l, rate: l.rate ?? pct(l.answered, l.calls) })),
    },
    outgoing: { total: out.total || 0, extensions: out.extensions || 0, answered: out.answered ?? null, answer_rate: out.answer_rate ?? null },
    averages: raw.averages || {},
    by_hour: byHour,
    by_extension: raw.by_extension || [],
    rank,
    highlights: raw.highlights || null,
    // Backend coverage ledger (analytics source): notes, warnings and whether
    // the window is complete enough for client delivery.
    coverage_note: raw.coverage_note || '',
    warnings: raw.warnings || [],
    deliverable: raw.deliverable ?? null,
    metric_coverage: raw.metric_coverage || null,
    // Group payloads carry a ranked site table and the lunchtime rows already.
    by_site: (raw.by_site || []).map((s) => ({ ...s, rate: s.rate ?? pct(s.answered, s.incoming) })),
    lunchtime: raw.lunchtime || null,
  };
}

// True when the backend's analytics coverage covers the window at all. A
// response whose coverage ledger is "none" for every practice carries no
// call-history data (scope not granted / import not run) and the live tab is
// better served by the webhook build.
export const hasAnalyticsCoverage = (raw) => {
  const ledger = Array.isArray(raw?.coverage) ? raw.coverage : [];
  if (!ledger.length) return raw?.source === 'analytics' && Boolean(raw?.incoming?.total);
  return ledger.some((c) => c && c.status && c.status !== 'none');
};

// Group endpoint payload ({practices, sites, group, ...}) → page model.
export function normaliseGroupPerformance(raw, fallbackName = 'All practices') {
  if (!raw || !raw.group) return null;
  const model = normalisePerformance({ ...raw.group, source: raw.source, coverage: raw.coverage, coverage_note: raw.coverage_note, warnings: raw.warnings, deliverable: raw.deliverable, practice_name: fallbackName }, fallbackName);
  model.sites = (raw.sites || []).map((s) => normalisePerformance(s, s.name));
  if (!model.by_site.length) model.by_site = aggregateGroup(model.sites).by_site;
  return model;
}

// Group view: the per-site table, group totals and group hour profile.
export function aggregateGroup(sites) {
  const list = sites.filter(Boolean);
  const total = (f) => list.reduce((sum, s) => sum + (f(s) || 0), 0);
  const incomingTotal = total((s) => s.incoming.total);
  const answeredTotal = total((s) => s.incoming.answered);
  const bySite = list
    .map((s) => ({ name: s.practice_name, incoming: s.incoming.total, answered: s.incoming.answered, unanswered: s.incoming.unanswered, rate: s.incoming.answer_rate, outgoing: s.outgoing.total, extensions: s.outgoing.extensions, by_line: s.incoming.by_line }))
    .sort((a, b) => b.rate - a.rate)
    .map((s, i) => ({ ...s, rank: i + 1 }));
  const hours = new Map(REPORT_HOURS.map((h) => [h, emptyHour(h)]));
  list.forEach((s) => s.by_hour.forEach((b) => {
    const bucket = hours.get(b.hour) || emptyHour(b.hour);
    ['calls', 'answered', 'unanswered', 'direct_calls', 'direct_answered'].forEach((k) => { bucket[k] += b[k] || 0; });
    hours.set(b.hour, bucket);
  }));
  const byHour = [...hours.values()].sort((a, b) => a.hour - b.hour).map((b) => ({ ...b, rate: pct(b.answered, b.calls), direct_rate: pct(b.direct_answered, b.direct_calls) }));
  const outgoingTotal = total((s) => s.outgoing.total);
  return {
    source: list.every((s) => s.source === 'analytics') ? 'analytics' : 'webhook',
    practice_name: 'All practices',
    incoming: { total: incomingTotal, answered: answeredTotal, unanswered: incomingTotal - answeredTotal, answer_rate: pct(answeredTotal, incomingTotal), overflowed: null, by_line: [] },
    outgoing: { total: outgoingTotal, extensions: total((s) => s.outgoing.extensions) },
    averages: {
      ring_seconds: avg(list.map((s) => s.averages.ring_seconds)),
      incoming_length_seconds: avg(list.map((s) => s.averages.incoming_length_seconds)),
      outgoing_length_seconds: avg(list.map((s) => s.averages.outgoing_length_seconds)),
    },
    by_hour: byHour,
    by_extension: [],
    by_site: bySite,
    highlights: null,
  };
}

// The "Four things the data points to" cards, computed when the backend does
// not send its own. Plain sentences, numbers from the model only.
export function buildHighlights(model) {
  if (!model || !model.incoming.total) return [];
  const items = [];
  const sites = model.by_site || [];
  if (sites.length > 1) {
    const worst = [...sites].sort((a, b) => b.unanswered - a.unanswered).slice(0, 3);
    const missed = worst.reduce((s, x) => s + x.unanswered, 0);
    if (model.incoming.unanswered > 0) items.push({ key: 'gap', title: `${worst.length} sites drive the gap`, body: `${worst.map((w) => w.name).join(', ')} account for ${missed} of ${model.incoming.unanswered} unanswered calls (${pct(missed, model.incoming.unanswered)}%).`, tone: 'warning' });
    const best = sites[0];
    if (best) items.push({ key: 'benchmark', title: 'The benchmark exists', body: `${best.name} answers ${best.rate}% of incoming calls, the rate the other sites can aim for.`, tone: 'dark' });
  }
  const newLine = (model.incoming.by_line || []).find((l) => /new/i.test(l.label));
  if (newLine && newLine.missed > 0) items.push({ key: 'new-patients', title: 'New-patient calls missed', body: `${newLine.missed} unanswered on the new-patient line; it answers ${newLine.rate}%.`, tone: 'warning' });
  const lunch = model.by_hour.find((b) => b.hour === 13);
  const morning = model.by_hour.filter((b) => b.hour >= 8 && b.hour < 13);
  const mCalls = morning.reduce((s, b) => s + b.calls, 0);
  const mAns = morning.reduce((s, b) => s + b.answered, 0);
  if (lunch && lunch.calls > 0 && mCalls > 0 && lunch.rate < pct(mAns, mCalls)) {
    const sides = model.by_hour.filter((b) => b.hour === 12 || b.hour === 14);
    const sideAvg = sides.length ? sides.reduce((s, b) => s + b.direct_calls, 0) / sides.length : 0;
    const drop = sideAvg > 0 ? Math.round(((sideAvg - lunch.direct_calls) / sideAvg) * 100) : 0;
    items.push({ key: 'lunch', title: 'Lunchtime cover', body: `Answer rate falls to ${lunch.rate}% at 13:00, against ${pct(mAns, mCalls)}% through the morning.${drop > 0 ? ` Outgoing calls drop ${drop}% in the same hour.` : ''}`, tone: 'info' });
  }
  return items.slice(0, 4);
}

export const lunchtimeRows = (model) => (model?.lunchtime?.length ? model.lunchtime.map((b) => ({ ...b, direct_calls: b.direct_calls ?? b.outgoing_calls ?? 0, rate: b.rate ?? 0, direct_rate: b.direct_rate ?? 0 })) : (model?.by_hour || []).filter((b) => LUNCH_HOURS.includes(b.hour)));

export const performanceKpis = (model) => {
  if (!model) return [];
  const inc = model.incoming;
  return [
    { key: 'incomingCalls', label: 'Incoming calls', value: inc.total, color: 'blue', subText: `${inc.answered.toLocaleString()} answered`, hideDelta: true, clickable: false, icon: 'solar:call-received-rounded-linear' },
    { key: 'answerRate', label: 'Answer rate', value: `${inc.answer_rate}%`, color: 'green', subText: model.rank ? `Rank ${model.rank.position} of ${model.rank.of}` : 'Incoming calls answered', hideDelta: true, clickable: false, icon: 'solar:check-circle-linear' },
    { key: 'unansweredCalls', label: 'Unanswered calls', value: inc.unanswered, color: 'red', subText: inc.overflowed != null ? `${inc.overflowed} overflowed` : 'Incoming, all lines', hideDelta: true, clickable: false, icon: 'solar:phone-calling-rounded-linear' },
    { key: 'outgoingCalls', label: 'Outgoing calls', value: model.outgoing.total, color: 'purple', subText: model.outgoing.extensions ? `From ${model.outgoing.extensions} extensions` : 'Made by the team', hideDelta: true, clickable: false, icon: 'solar:call-transfer-rounded-linear' },
    { key: 'avgRing', label: 'Average ring time', value: formatSeconds(model.averages.ring_seconds), color: 'yellow', subText: 'Incoming calls', hideDelta: true, clickable: false, icon: 'solar:clock-circle-linear' },
    { key: 'avgLength', label: 'Average call length', value: formatSeconds(model.averages.incoming_length_seconds), color: 'cyan', subText: model.averages.outgoing_length_seconds ? `Outgoing average ${formatSeconds(model.averages.outgoing_length_seconds)}` : 'Answered incoming calls', hideDelta: true, clickable: false, icon: 'solar:chat-round-call-linear' },
  ];
};
